import { useState, useEffect } from "react";

import { format } from "date-fns";
import { Shield, ShieldCheck, AlertTriangle, RefreshCw, History, ChevronDown, Info } from "lucide-react";

import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "@/components/ui/sonner";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { supabase } from "@/integrations/supabase/client";

interface CommunicationCodeManagementProps {
  userId: string;
}

interface CodeHistory {
  id: string;
  old_code: string | null;
  new_code: string;
  regenerated_at: string;
  reason: string | null;
}

export const CommunicationCodeManagement = ({ userId }: CommunicationCodeManagementProps) => {
  const { execute: invoke, isLoading: regenerating } = useEdgeFunction({ auth: true });
  const [code, setCode] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [history, setHistory] = useState<CodeHistory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCodeData();
  }, [userId]);

  const fetchCodeData = async () => {
    try {
      // Fetch current code
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('communication_code, communication_code_updated_at')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      setCode(profile.communication_code);
      setUpdatedAt(profile.communication_code_updated_at);

      // Fetch history
      const { data: historyData, error: historyError } = await supabase
        .from('communication_code_history')
        .select('*')
        .eq('user_id', userId)
        .order('regenerated_at', { ascending: false })
        .limit(10);

      if (historyError) throw historyError;
      setHistory(historyData || []);
    } catch (error) {
      console.error('Error fetching code data:', error);
      toast.error('Failed to load communication code');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateCode = async () => {
    const { data, error } = await invoke('regenerate-communication-code', {
      body: { reason: 'user_requested' },
      showErrorToast: true,
    });

    if (error) return;

    toast.success(data.message);
    setCode(data.new_code);
    setUpdatedAt(new Date().toISOString());
    
    // Refresh data to get history
    fetchCodeData();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Anti-Phishing Protection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-24 bg-muted rounded-lg" />
            <div className="h-12 bg-muted rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-green-500/20">
              <Shield className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <CardTitle>Anti-Phishing Protection</CardTitle>
              <CardDescription>
                Your unique security code appears in all emails from us
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Code Display */}
        <div className="border-2 border-green-500/30 bg-linear-to-br from-green-500/10 to-background rounded-lg p-6 text-center">
          <p className="text-sm text-muted-foreground mb-2">Your Security Code</p>
          <div className="text-3xl font-bold font-mono tracking-wider text-green-600 dark:text-green-400 mb-2">
            {code || 'Not Set'}
          </div>
          <p className="text-xs text-muted-foreground">
            {updatedAt ? `Set on ${format(new Date(updatedAt), 'PPP')}` : 'Never updated'}
          </p>
        </div>

        {/* Educational Content */}
        <Alert>
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>How This Protects You</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4 space-y-1 text-sm mt-2">
              <li>Every legitimate email from us includes your unique code at the top</li>
              <li>Phishing emails cannot duplicate your code - they don't have access to it</li>
              <li>If an email lacks your exact code, it's fake - report it immediately</li>
              <li>Your code is private and only changes when you regenerate it</li>
            </ul>
          </AlertDescription>
        </Alert>

        {/* Regenerate Section */}
        <div className="pt-4 border-t space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h4 className="font-medium text-sm mb-1">Regenerate Your Code</h4>
              <p className="text-sm text-muted-foreground">
                Only regenerate if you suspect your code has been compromised or shared accidentally.
                This will change the code that appears in all future emails.
              </p>
            </div>
          </div>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="outline" 
                className="w-full" 
                isLoading={regenerating}
                loadingText="Regenerating..."
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Regenerate Communication Code
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Regenerate Security Code?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will change your communication code to a new random phrase. 
                  All future emails will display the new code. Old emails will still show the previous code.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleRegenerateCode}>
                  Yes, Regenerate Code
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Code History */}
        {history.length > 0 && (
          <Collapsible>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full">
              <History className="h-4 w-4" />
              <span className="flex-1 text-left">View Code History ({history.length})</span>
              <ChevronDown className="h-4 w-4 transition-transform duration-200" />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <div className="space-y-2 border rounded-lg p-4 bg-muted/30">
                {history.map((item) => (
                  <div key={item.id} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                    <div className="flex-1">
                      {item.old_code && (
                        <span className="line-through text-muted-foreground mr-2">{item.old_code}</span>
                      )}
                      <span className="font-mono font-semibold">{item.new_code}</span>
                    </div>
                    <div className="text-xs text-muted-foreground text-right">
                      {format(new Date(item.regenerated_at), 'PP')}
                      {item.reason && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          {item.reason}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* FAQ Section */}
        <div className="pt-4 border-t space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Info className="h-4 w-4" />
            Frequently Asked Questions
          </div>
          
          <div className="space-y-3 text-sm">
            <div>
              <h5 className="font-medium mb-1">What if I see an email without my code?</h5>
              <p className="text-muted-foreground">
                It's a phishing attempt. Delete it immediately and report it to security@yourdomain.com. 
                Do not click any links in that email.
              </p>
            </div>
            
            <div>
              <h5 className="font-medium mb-1">Can others see my code?</h5>
              <p className="text-muted-foreground">
                Your code is private to you. It only appears in emails sent directly to you. 
                No one else can see or use your code.
              </p>
            </div>
            
            <div>
              <h5 className="font-medium mb-1">Should I share my code with support?</h5>
              <p className="text-muted-foreground">
                Never share your code with anyone. Our support team will never ask for it. 
                If someone asks for your code, it's a scam.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
